import { ApplePaySession } from 'braintree-web';
import { ForageService } from 'src/services/ForageService';
import { StripeApplePayService } from 'src/services/StripeApplePayService';
import { getCookie, setCookie, setCookieWithExpiry } from 'src/utils/cookies';
import { getDeviceInfo } from 'src/utils/device';
import { getVisibleElementById, getVisibleElementsById } from 'src/utils/dom';
import { SiftEvents } from 'src/utils/siftEvents';

import { Api } from '../../apis/api';
import { GaPaymentButtonType } from '../../apis/gtm';
import { Klaviyo } from '../../apis/klaviyo';
import { klarnaLocales } from '../../constants/payments';
import { BriantreeService } from '../../services/BraintreeService';
import { KlarnaService } from '../../services/KlarnaService';
import {
  ICcFeatureFlag,
  IImportLeadPayload,
  IImportOrderCreditCardPayload,
  IImportOrderKlarnStripePayload,
  IImportOrderPaypalPayload,
  IPayPalConfirmProps,
  IPpFeatureFlag,
  PaySource,
} from '../../types/api';
import { OrderFlow } from '../../types/order';
import { runScript } from '../../utils/eval';
import { getPersistedUrlParameters, navigate } from '../../utils/navigation';
import {
  appendUrlParams,
  createUrl,
  getSlug,
  getUrlParameters,
} from '../../utils/url';
import {
  getAppUtms,
  getCustomCookieParams,
  getCustomParams,
} from '../../utils/utms';
import { isValidUrl, validateLead } from '../../utils/validations';
import { FunnelController } from '../business/FunnelController';
import { IBuyButtonIds } from '../forms/CheckoutFormController';
import { UiController } from '../ui/UiController';

export interface ICreditCardInfo {
  cvv: string;
  number: string;
  cardType: string;
  expirationYear: string;
  expirationMonth: string;
}

let NON_RESPONSIVE_TS: NodeJS.Timeout | null = null;

export class CheckoutController {
  private fc: FunnelController;
  private klarna?: KlarnaService;
  private braintree?: BriantreeService;
  private stripeApplePay?: StripeApplePayService;
  private ccConfig: ICcFeatureFlag = window.FLAGS.cc_profile as ICcFeatureFlag;
  private ppConfig: IPpFeatureFlag = window.FLAGS.pp_profile as IPpFeatureFlag;
  private confirm_pp_p: any;

  constructor(fc: FunnelController) {
    this.fc = fc;
    if (window.DEBUG) {
      console.log('FF_FLAGS', window.FLAGS);
    }
  }

  private getCardGwId = (cardType: string) => {
    if (!this.ccConfig) return undefined;
    if (window.BRAND !== this.ccConfig.brand.toLowerCase()) return undefined;

    switch (cardType) {
      case 'visa':
        return this.ccConfig.visa;
      case 'mastercard':
        return this.ccConfig.mastercard;
      case 'american-express':
        return this.ccConfig.amex_discovery;
      case 'discover':
        return this.ccConfig.amex_discovery;
      default:
        return this.ccConfig.mastercard;
    }
  };

  getParams = (cardInfo?: ICreditCardInfo) => {
    const auid = getAppUtms().custom_order_auid;

    // Custom URL params, that will be added to payloads
    const custom_order_m_ab_var = getAppUtms().custom_order_m_ab_var;
    const custom_order_m_ab_name = getAppUtms().custom_order_m_ab_name;
    const custom_order_ab_name = getAppUtms().custom_order_ab_name;
    const custom_order_ab_var = getAppUtms().custom_order_ab_var;

    const lead = this.fc.lead.lead;
    const sessionId = this.fc.config.sessionId || this.fc.lead.lead.sessionId;
    const orderId = this.fc.lead.lead.orderId as string;
    const product = this.fc.getSelectedProduct();
    const paypalType = this.ppConfig?.paypal_type || 'default';
    const paypalMid = this.ppConfig?.paypal_mid || this.fc.config.paypalId;

    // Redirect to first upsell onsuccess
    const successRedirect = this.fc.config?.redirectTo;
    const serverRedirectUrl = this.fc.config?.ppSuccessRedirectUrl;
    const taxExemption = this.ccConfig?.tax === 'no_tax' ? 'no_tax' : undefined;
    const errorRedirect = createUrl(location.href, {
      cancel_url: '1',
    });
    const forceLoadBalancerId = cardInfo && this.getCardGwId(cardInfo.cardType);
    let forceMerchantId: number | undefined = undefined;
    if (this.fc.config.forceMerchantId) {
      forceMerchantId = parseInt(this.fc.config.forceMerchantId, 10);
    }

    if (!product) {
      throw new Error('Error: Products is undefined');
    }
    if (!successRedirect) {
      throw new Error('Error: successRedirect is undefined');
    }
    if (!serverRedirectUrl) {
      throw new Error('Error: serverRedirUrl is undefined');
    }

    // Redirect to PM eg Paypal, Klarna, Reach
    const pmExternalRedirect = createUrl(serverRedirectUrl, {
      redirectTo: successRedirect,
    });

    return {
      auid,
      lead,
      orderId,
      product,
      paypalMid,
      sessionId,
      paypalType,
      taxExemption,
      errorRedirect,
      forceMerchantId,
      successRedirect,
      pmExternalRedirect,
      forceLoadBalancerId,
      customPayloadParams: {
        custom_order_m_ab_var,
        custom_order_m_ab_name,
        custom_order_ab_name,
        custom_order_ab_var,
      },
    };
  };

  /**
   * Set a cookie with the currently selected product
   */
  private _setSelectedProductCookie = (key = 'selected', expiration = 10) => {
    setCookieWithExpiry(
      key,
      {
        product: this.fc.getSelectedProduct(),
        subscription: window.subSelected,
      },
      expiration,
    );
  };

  /**
   * Add extra parameters to the order payload
   */
  private _addExtraParameters = (payload: any, paymentMethod?: string) => {
    if (payload) {
      if (
        payload.custom_order_ev_click_id &&
        payload.custom_order_ev_click_id.includes(',')
      ) {
        payload.custom_order_ev_click_id =
          payload.custom_order_ev_click_id.split(',')[0];
      }
      const evClickId = sessionStorage.getItem('ev_click_id');
      if (evClickId) {
        payload.custom_order_ev_click_id = evClickId.split(',')[0];
      }

      if (
        payload.custom_order_ev_oid &&
        payload.custom_order_ev_oid.includes(',')
      ) {
        payload.custom_order_ev_oid = payload.custom_order_ev_oid.split(',')[0];
      }
      const evOid = sessionStorage.getItem('ev_oid');
      if (evOid) {
        payload.custom_order_ev_oid = evOid.split(',')[0];
      }

      if (paymentMethod) {
        appendUrlParams({
          pm: paymentMethod,
        });
        sessionStorage.setItem('pm', paymentMethod);
      }

      const urlParams = sessionStorage.getItem('url_params');
      if (urlParams) {
        try {
          const urlParamsData = JSON.parse(urlParams);
          if (urlParamsData && urlParamsData.force && !payload.custom_order_force) {
            payload.custom_order_force = urlParamsData.force;
          }
        } catch (error) {
          console.log(error);
        }
      }
    }
  };

  maybeInitKlarna = (country?: string) => {
    const slug = getSlug();
    const enabled =
      this.fc.config.klarnaEnabled &&
      klarnaLocales.some(locale => slug?.startsWith(locale));
    if (enabled) {
      waitFor('Klarna', () => {
        this.klarna = new KlarnaService(this.fc, '#klarna-payments-container');
        this.klarna.initSession(country);
      });
      setTimeout(() => {
        if (!this.klarna) {
          Errors.report(new Error('Klarna not initialized after 2s'));
        }
      }, 2000);
    }
  };

  maybeInitBraintree = (buttonIds: IBuyButtonIds) => {
    waitFor('google', async () => {
      this.braintree = await BriantreeService.init(this.fc, buttonIds);
      this.braintree.maybeRenderButtons();
    });
  };

  maybeInitStripeApplePay = async () => {
    const { stripeApplePayEnabled } = this.fc.config;
    if (stripeApplePayEnabled) {
      this.stripeApplePay = await StripeApplePayService.init(this.fc);
      this.stripeApplePay.maybeRenderButtons();
      this.stripeApplePay.createApplePayment(this, false);
    }
  };

  createCreditCardOrder = async (cardInfo: ICreditCardInfo) => {
    try {
      const {
        auid,
        lead,
        orderId,
        product,
        sessionId,
        taxExemption,
        errorRedirect,
        successRedirect,
        pmExternalRedirect,
        forceLoadBalancerId,
        customPayloadParams,
      } = this.getParams(cardInfo);

      validateLead(lead);

      // Phone number is required for delivery in Israel
      const phoneNumber = UiController.getPhoneNumber('phoneNumber');

      if (
        (window.GEO.country === 'IL' || this.fc.config?.phoneRequired) &&
        !phoneNumber
      ) {
        throw new Error('Phone number is required');
      }

      const cardYear =
        cardInfo.expirationYear.length === 2
          ? `20${cardInfo.expirationYear}`
          : cardInfo.expirationYear;

      // If subscribtion product selected
      const campaignId = window.subSelected
        ? this.fc.config.subscriptionCampaignId || this.fc.config.campaignId
        : this.fc.config.campaignId;

      const orderData: IImportOrderCreditCardPayload = {
        client: window.BRAND,
        paySource: PaySource.CREDITCARD,
        orderId,
        campaignId,
        taxExemption,
        companyName: 'unfullfiled',
        fullUrl: location.href,
        errorRedirectsTo: errorRedirect,
        redirectsTo: pmExternalRedirect,
        salesUrl: location.href,
        cardNumber: cardInfo.number,
        cardYear,
        cardMonth: cardInfo.expirationMonth,
        cardSecurityCode: cardInfo.cvv,
        product1_id: String(product.id),
        product1_price: product.discountPrice
          ? product.finalPrice - product.discountPrice
          : product.finalPrice,
        product1_shipPrice: product.shippingPrice || 0,
        address1: lead.address1,
        city: lead.city,
        forceLoadBalancerId,
        postalCode: lead.postalCode,
        shipPostalCode: lead.shipPostalCode,
        emailAddress: lead.emailAddress,
        custom1: getUrlParameters('ad_id'),
        custom2: 'credit_card',
        custom3: `${window.location.origin + window.location.pathname}`,
        sourceValue3: auid,
        custom_order_auid: auid,
        custom_order_sid: sessionId,
        custom_order_paymentType: `credit_card`,
        custom_order_initial: product.id.toString(),
        custom_order_freeGift: product?.freeGift || false,
        custom_order_paymentMethod: PaySource.CREDITCARD,
        custom_order_order_flow: window.IS_1_CLICK
          ? OrderFlow.CHECKOUT_1CLICK
          : OrderFlow.CHECKOUT,
        note: 'initial',
        userAgent: navigator?.userAgent,
        ipAddress: window.GEO?.ip || '::1',
        custom_order_force: new URL(location.href).searchParams.get('force') || '',
        ...customPayloadParams,
      };

      if (window.DEBUG) {
        console.log(product);
      }

      this.fc.addAddonsToPurchasePayload(orderData);
      this._addExtraParameters(orderData, 'creditcard');

      NON_RESPONSIVE_TS = Errors.maybeReportNonResponsivePaymentButton(
        PaySource.CREDITCARD,
      );

      SiftEvents.createOrder(
        { ...this.fc.lead.lead, ...orderData },
        {
          paymentType: '$credit_card',
          cardInfo,
          returnScore: false,
        },
      );

      const res = await Api.importCreditCardOrder(orderData);
      if (window.DEBUG) {
        console.log('res: ', res);
      }

      SiftEvents.trackTransaction(orderData, res, {
        returnScore: true,
        cardInfo,
      });

      if (isValidUrl(res.url)) {
        window.location.href = res.url;
      } else if (res.script) {
        runScript(res.script);
      } else {
        navigate(successRedirect, this.fc.lead.lead);
      }
    } catch (e: any) {
      Errors.report(e, {
        geo: window.GEO,
        config: this.fc.config || 'undefined',
        lead: this.fc.lead.lead || 'undefined',
        product: this.fc.getSelectedProduct() || 'undefined',
        productIdx: window['productIdx'].value || 'undefined',
        paySource: PaySource.CREDITCARD,
      });

      const { orderId } = this.getParams(cardInfo);
      SiftEvents.trackFailedTransaction(orderId, null, {
        paymentType: PaySource.CREDITCARD,
        errorMessage: `${e?.response?.data?.message[0]}`,
        cardInfo,
      });
    } finally {
      NON_RESPONSIVE_TS && clearTimeout(NON_RESPONSIVE_TS);
    }
  };

  createKlarnaStripeOrder = async () => {
    try {
      const {
        auid,
        lead,
        orderId,
        product,
        sessionId,
        errorRedirect,
        pmExternalRedirect,
        customPayloadParams,
        forceMerchantId,
      } = this.getParams();

      const phoneInput = getVisibleElementById<'input'>('phoneNumber');

      const orderData: IImportOrderKlarnStripePayload = {
        orderId,
        client: window.BRAND,
        fullUrl: location.href,
        salesUrl: location.href,
        companyName: 'unfullfiled',
        paySource: PaySource.PREPAID,
        prepaidType: PaySource.KLARNA,
        campaignId: this.fc.config.campaignId,
        product1_id: String(product.id),
        product1_price: product.discountPrice
          ? product.finalPrice - product.discountPrice
          : product.finalPrice,
        product1_shipPrice: product.shippingPrice || 0,
        redirectsTo: pmExternalRedirect,
        errorRedirectsTo: errorRedirect,
        emailAddress: lead.emailAddress || '',
        custom1: getUrlParameters('ad_id'),
        phoneNumber: phoneInput?.value,
        forceMerchantId: forceMerchantId || 117,
        custom2: 'klarna_stripe',
        custom3: `${window.location.origin + window.location.pathname}`,
        sourceValue3: auid,
        custom_order_auid: auid,
        custom_order_sid: sessionId,
        custom_order_paymentType: `klarna_stripe`,
        custom_order_initial: product.id.toString(),
        custom_order_freeGift: product?.freeGift || false,
        custom_order_paymentMethod: PaySource.STRIPE_KLARNA,
        custom_order_order_flow: window.IS_1_CLICK
          ? OrderFlow.CHECKOUT_1CLICK
          : OrderFlow.CHECKOUT,
        note: 'initial',
        userAgent: navigator?.userAgent,
        ipAddress: window.GEO?.ip || '::1',
        custom_order_force: new URL(location.href).searchParams.get('force') || '',
        ...customPayloadParams,
      };

      if (window.DEBUG) {
        console.log(product);
      }

      this.fc.addAddonsToPurchasePayload(orderData);
      this._addExtraParameters(orderData, 'klarna_stripe');

      NON_RESPONSIVE_TS = Errors.maybeReportNonResponsivePaymentButton(
        PaySource.STRIPE_KLARNA,
      );

      // Track create_order event in Sift with orderData containing all products
      // Use isExpressPayment flag for express PayPal to allow sending event without orderId
      SiftEvents.createOrder(
        { ...this.fc.lead.lead, ...orderData },
        {
          paymentType: PaySource.KLARNA,
          returnScore: false,
        },
      );

      this._setSelectedProductCookie();

      const res = await Api.importKlarnaStripeOrder(orderData);
      if (window.DEBUG) {
        console.log(res);
      }

      SiftEvents.trackTransaction(orderData, res, {
        returnScore: true,
        paymentType: PaySource.KLARNA,
      });

      if (res.url && isValidUrl(res.url)) {
        window.location.href = res.url;
      }
    } catch (e: any) {
      Errors.report(e, {
        geo: window.GEO,
        config: this.fc.config || 'undefined',
        lead: this.fc.lead.lead || 'undefined',
        product: this.fc.getSelectedProduct() || 'undefined',
        productIdx: window['productIdx'].value || 'undefined',
      });

      const { orderId } = this.getParams();
      SiftEvents.trackFailedTransaction(orderId, null, {
        paymentType: PaySource.KLARNA,
        errorMessage: `${e?.response?.data?.message[0]}`,
      });
    } finally {
      NON_RESPONSIVE_TS && clearTimeout(NON_RESPONSIVE_TS);
    }
  };

  createPayPalOrder = async () => {
    try {
      const { paypalMid, paypalType } = this.getParams();
      if (!paypalMid) {
        throw new Error('Error: paypalId is undefined');
      }

      NON_RESPONSIVE_TS = Errors.maybeReportNonResponsivePaymentButton(
        PaySource.PAYPAL,
      );

      this._setSelectedProductCookie();

      if (paypalType === 'reach') {
        await this.createReachPayPalOrder(paypalMid);
      } else {
        await this.createDefaultPayPalOrder(paypalMid, paypalType);
      }
    } catch (e) {
      Errors.report(e, {
        geo: window.GEO,
        config: this.fc.config || 'undefined',
        lead: this.fc.lead.lead || 'undefined',
        product: this.fc.getSelectedProduct() || 'undefined',
        productIdx: window['productIdx'].value || 'undefined',
      });
    } finally {
      NON_RESPONSIVE_TS && clearTimeout(NON_RESPONSIVE_TS);
    }
  };

  private createDefaultPayPalOrder = async (
    paypalMid: number,
    paypalType?: string,
  ) => {
    const params = new URLSearchParams(document.location.search);
    const {
      auid,
      lead,
      orderId,
      product,
      sessionId,
      errorRedirect,
      pmExternalRedirect,
      customPayloadParams,
    } = this.getParams();

    if (this.fc.config.expressPaypal) {
      paypalType = 'express';
    }

    const paypalBillerId = paypalType === 'express' ? paypalMid : undefined;
    const custom2 =
      paypalType === 'default'
        ? `paypal_${paypalMid}`
        : `paypal_${paypalType}_${paypalMid}`;
    if (paypalType === 'express') {
      const optinCheckboxes = getVisibleElementsById<'input'>('optinCheckbox');
      const optinCheckbox =
        optinCheckboxes?.filter(el => el?.checked)[0] || optinCheckboxes[0];

      const storage = ForageService.getInstance();
      if (paypalBillerId)
        await storage.setValue('express_paypal_mid', paypalBillerId.toString());
      if (sessionId) await storage.setValue('express_session_id', sessionId);
      if (product.id)
        await storage.setValue('express_product_id', product.id.toString());
      if (optinCheckbox) {
        const optinValue = optinCheckbox ? `${optinCheckbox.checked}` : '0';
        setCookie('marketing_opt_in', optinValue);
        await storage.setValue('marketing_opt_in', optinValue);
      }
    }
    if (paypalType !== 'express') {
      validateLead(lead);
    }

    const phoneNumber = UiController.getPhoneNumber('phoneNumber');

    if (
      (window.GEO.country === 'IL' || this.fc.config?.phoneRequired) &&
      !phoneNumber
    ) {
      throw new Error('Phone number is required');
    }

    // If subscribtion product selected
    const campaignId = window.subSelected
      ? this.fc.config.subscriptionCampaignId || this.fc.config.campaignId
      : this.fc.config.campaignId;

    let finalOrderId: string | undefined = orderId;
    if (paypalType === 'express') {
      finalOrderId = undefined;
    }

    const orderData: IImportOrderPaypalPayload = {
      orderId: finalOrderId,
      campaignId,
      paypalBillerId,
      client: window.BRAND,
      fullUrl: location.href,
      salesUrl: location.href,
      phoneNumber,
      companyName: 'unfullfiled',
      paySource: PaySource.PAYPAL,
      forceMerchantId: paypalMid,
      product1_id: String(product.id),
      product1_price: product.discountPrice
        ? product.finalPrice - product.discountPrice
        : product.finalPrice,
      product1_shipPrice: product.shippingPrice || 0,
      redirectsTo: pmExternalRedirect,
      errorRedirectsTo: errorRedirect,
      emailAddress: lead.emailAddress || '',
      custom2,
      custom3: `${window.location.origin + window.location.pathname}`,
      affId: params.get('affid') || undefined,
      custom1: getUrlParameters('ad_id'),
      sourceValue3: auid,
      custom_order_auid: auid,
      custom_order_sid: sessionId,
      custom_order_paymentType: custom2,
      custom_order_initial: product.id.toString(),
      custom_order_paymentMethod: PaySource.PAYPAL,
      custom_order_freeGift: product?.freeGift || false,
      note: 'initial',
      custom_order_order_flow: window.IS_1_CLICK
        ? OrderFlow.CHECKOUT_1CLICK
        : OrderFlow.CHECKOUT,
      userAgent: navigator?.userAgent,
      ipAddress: window.GEO?.ip || '::1',
      custom_order_force: new URL(location.href).searchParams.get('force') || '',
      ...customPayloadParams,
    };

    if (window.DEBUG) {
      console.log(product);
    }

    this.fc.addAddonsToPurchasePayload(orderData);
    this._addExtraParameters(orderData, 'paypal_default');

    // Track create_order event in Sift with orderData containing all products
    // For express PayPal, we need to use isExpressPayment flag since orderId isn't available yet
    SiftEvents.createOrder(
      { ...this.fc.lead.lead, ...orderData },
      {
        isExpressPayment: paypalType === 'express',
        paymentType: PaySource.PAYPAL,
        returnScore: false,
      },
    );

    const res = await Api.importPaypalOrder(orderData);
    if (window.DEBUG) {
      console.log(res);
    }

    if (isValidUrl(res.url) || isValidUrl(res.paypalUrl)) {
      getPersistedUrlParameters();
      window.location.href = isValidUrl(res.url) ? res.url : res.paypalUrl;
    }
  };

  private createReachPayPalOrder = async (paypalMid: number) => {
    const params = new URLSearchParams(document.location.search);
    const {
      auid,
      lead,
      orderId,
      product,
      sessionId,
      errorRedirect,
      pmExternalRedirect,
      customPayloadParams,
    } = this.getParams();

    // If subscribtion product selected
    const campaignId = window.subSelected
      ? this.fc.config.subscriptionCampaignId || this.fc.config.campaignId
      : this.fc.config.campaignId;

    const orderData: IImportOrderPaypalPayload = {
      orderId,
      campaignId,
      sourceValue3: auid,
      client: window.BRAND,
      fullUrl: location.href,
      salesUrl: location.href,
      companyName: 'unfullfiled',
      paySource: PaySource.PREPAID,
      forceMerchantId: paypalMid,
      prepaidType: PaySource.WITHREACH_PAYPAL,
      product1_id: String(product.id),
      product1_price: product.discountPrice
        ? product.finalPrice - product.discountPrice
        : product.finalPrice,
      product1_shipPrice: product.shippingPrice || 0,
      redirectsTo: pmExternalRedirect,
      errorRedirectsTo: errorRedirect,
      emailAddress: lead.emailAddress || '',
      custom2: `paypal_reach_${paypalMid}`,
      custom3: `${window.location.origin + window.location.pathname}`,
      affId: params.get('affid') || undefined,
      custom1: getUrlParameters('ad_id'),
      custom_order_auid: auid,
      custom_order_sid: sessionId,
      custom_order_paymentType: `paypal_reach_${paypalMid}`,
      custom_order_initial: product.id.toString(),
      custom_order_paymentMethod: PaySource.WITHREACH_PAYPAL,
      custom_order_freeGift: product?.freeGift || false,
      note: 'initial',
      custom_order_order_flow: window.IS_1_CLICK
        ? OrderFlow.CHECKOUT_1CLICK
        : OrderFlow.CHECKOUT,
      userAgent: navigator?.userAgent,
      ipAddress: window.GEO?.ip || '::1',
      custom_order_force: new URL(location.href).searchParams.get('force') || '',
      ...customPayloadParams,
    };

    if (window.DEBUG) {
      console.log(product);
    }

    this.fc.addAddonsToPurchasePayload(orderData);
    this._addExtraParameters(orderData, 'paypal_reach');

    // Track create_order event in Sift with orderData containing all products
    SiftEvents.createOrder(
      { ...this.fc.lead.lead, ...orderData },
      { paymentType: PaySource.WITHREACH_PAYPAL, returnScore: false },
    );

    const res = await Api.importPaypalOrder(orderData);
    if (window.DEBUG) {
      console.log(res);
    }

    if (res.script) {
      runScript(res.script);
    } else if (isValidUrl(res.url) || isValidUrl(res.paypalUrl)) {
      getPersistedUrlParameters();
      window.location.href = isValidUrl(res.url) ? res.url : res.paypalUrl;
    }
  };

  confirmPaypalOrder = async (props: IPayPalConfirmProps) => {
    UiController.showPpLoaderModal(true);
    const { customPayloadParams } = this.getParams();
    const auid = getAppUtms().custom_order_auid;
    const successRedirect = this.fc.config?.redirectTo;
    const { baToken, paypalMid, ppToken, product, sessionId } = props;
    const finalPaypalMid =
      paypalMid || this.ppConfig?.paypal_mid || this.fc.config.paypalId;
    const persistedUrlParameters = getPersistedUrlParameters();
    const params = new URLSearchParams(persistedUrlParameters);
    const customCustomerParams = getCustomParams(params);
    const urlQueryParameters = getCustomCookieParams();
    const marketingOptin = getCookie('marketing_opt_in');

    try {
      NON_RESPONSIVE_TS = Errors.maybeReportNonResponsivePaymentButton(
        PaySource.PAYPAL_CONFIRM,
      );

      this._setSelectedProductCookie();

      if (window.DEBUG) {
        console.log(product);
      }

      const orderData: any = {
        baToken,
        sessionId,
        token: ppToken,
        emailAddress: '',
        sourceValue3: auid,
        client: window.BRAND,
        campaignId: this.fc.config.campaignId,
        paypalBillerId: String(finalPaypalMid),
        product1_id: String(product.id),
        product1_price: product.discountPrice
          ? product.finalPrice - product.discountPrice
          : product.finalPrice,
        product1_shipPrice: product.shippingPrice || 0,
        page: 'checkoutPage',
        custom1: params.get('ad_id') || '',
        custom2: `paypal_express_${finalPaypalMid}`,
        custom3: `${window.location.origin + window.location.pathname}`,
        custom_order_sid: sessionId,
        custom_order_initial: product.id.toString(),
        custom_order_paymentMethod: PaySource.PAYPAL,
        custom_order_freeGift: product?.freeGift || false,
        custom_order_language: this.fc.config.locale,
        custom_order_paymentType: `paypal_express_${finalPaypalMid}`,
        custom_order_marketing_opt_in: `${marketingOptin}`,
        custom_order_order_flow: window.IS_1_CLICK
          ? OrderFlow.CHECKOUT_1CLICK
          : OrderFlow.CHECKOUT,
        note: 'initial',
        ...getDeviceInfo(),
        ...urlQueryParameters,
        userAgent: navigator?.userAgent,
        ipAddress: window.GEO?.ip || '::1',
        custom_order_force: new URL(location.href).searchParams.get('force') || '',
        ...customPayloadParams,
      };

      if (!orderData.custom_order_force) {
        orderData.custom_order_force = new URL(orderData.custom_order_http_referer).searchParams.get('force') || '';
      }

      this.fc.addAddonsToPurchasePayload(orderData);
      this._addExtraParameters(orderData, 'paypal_express');

      this.confirm_pp_p = orderData;

      const res = await Api.confirmPaypalPayment(orderData);
      if (window.DEBUG) {
        console.log(res);
      }

      SiftEvents.trackTransaction(orderData, res, {
        isExpressPayment: true,
        returnScore: true,
        paymentType: PaySource.PAYPAL,
      });

      const locale = this.fc.config?.locale || '';

      Klaviyo.trackStartedCheckoutV2(
        res.emailAddress,
        res.firstName || '',
        res.lastName || '',
        product,
        locale,
      );

      Api.updateCustomer({
        customerId: res.customerId,
        custom_user_agent: navigator.userAgent,
        custom_http_referer: `${window.location.origin}${window.location.pathname}?${persistedUrlParameters}`,
        ...customCustomerParams,
      });

      if (successRedirect) {
        navigate(successRedirect, { orderId: res?.orderId });
      }
    } catch (e: any) {
      Errors.report(e, {
        geo: window.GEO,
        inputProps: props,
        product: product || 'undefined',
        config: this.fc.config || 'undefined',
        lead: this.fc.lead.lead || 'undefined',
        productIdx: window['productIdx'].value || 'undefined',
      });

      const { orderId } = this.getParams();
      SiftEvents.trackFailedTransaction(orderId, this.confirm_pp_p, {
        paymentType: PaySource.PAYPAL,
        errorMessage: `${e?.response?.data?.message[0]}`,
      });
    } finally {
      NON_RESPONSIVE_TS && clearTimeout(NON_RESPONSIVE_TS);
    }
    UiController.showPpLoaderModal(false);
  };

  confirmPaypalOrderWOBaToken = async (props: IPayPalConfirmProps) => {
    UiController.showPpLoaderModal(true);
    const { customPayloadParams } = this.getParams();
    const auid = getAppUtms().custom_order_auid;
    const successRedirect = this.fc.config?.redirectTo;
    const { paypalMid, ppToken, product, sessionId, payerId } = props;
    const finalPaypalMid =
      paypalMid || this.ppConfig?.paypal_mid || this.fc.config.paypalId;
    const persistedUrlParameters = getPersistedUrlParameters();
    const params = new URLSearchParams(persistedUrlParameters);
    const customCustomerParams = getCustomParams(params);
    const urlQueryParameters = getCustomCookieParams();
    const marketingOptin = getCookie('marketing_opt_in');

    try {
      NON_RESPONSIVE_TS = Errors.maybeReportNonResponsivePaymentButton(
        PaySource.PAYPAL_CONFIRM,
      );

      this._setSelectedProductCookie();

      const orderData: any = {
        sessionId,
        token: ppToken,
        emailAddress: '',
        sourceValue3: auid,
        client: window.BRAND,
        campaignId: this.fc.config.campaignId,
        paypalBillerId: String(finalPaypalMid),
        product1_id: String(product.id),
        product1_price: product.finalPrice,
        product1_shipPrice: product.shippingPrice || 0,
        page: 'checkoutPage',
        custom1: params.get('ad_id') || '',
        custom2: `paypal_express_${finalPaypalMid}`,
        custom3: `${window.location.origin + window.location.pathname}`,
        custom_order_sid: sessionId,
        custom_order_initial: product.id.toString(),
        custom_order_paymentMethod: PaySource.PAYPAL,
        custom_order_freeGift: product?.freeGift || false,
        custom_order_language: this.fc.config.locale,
        custom_order_paymentType: `paypal_express_${finalPaypalMid}`,
        custom_order_marketing_opt_in: `${marketingOptin}`,
        custom_order_order_flow: window.IS_1_CLICK
          ? OrderFlow.CHECKOUT_1CLICK
          : OrderFlow.CHECKOUT,
        note: 'initial',
        ...getDeviceInfo(),
        ...urlQueryParameters,
        userAgent: navigator?.userAgent,
        ipAddress: window.GEO?.ip || '::1',
        payerId,
        custom_order_force: new URL(location.href).searchParams.get('force') || '',
        ...customPayloadParams,
      };

      if (!orderData.custom_order_force) {
        orderData.custom_order_force = new URL(orderData.custom_order_http_referer).searchParams.get('force') || '';
      }

      this.fc.addAddonsToPurchasePayload(orderData);
      this._addExtraParameters(orderData, 'paypal_express');

      this.confirm_pp_p = orderData;

      const res = await Api.confirmPaypalPayment(orderData);
      if (window.DEBUG) {
        console.log(res);
      }

      SiftEvents.trackTransaction(orderData, res, {
        isExpressPayment: true,
        returnScore: true,
        paymentType: PaySource.PAYPAL,
      });

      const locale = this.fc.config?.locale || '';

      Klaviyo.trackStartedCheckoutV2(
        res.emailAddress,
        res.firstName || '',
        res.lastName || '',
        product,
        locale,
      );

      Api.updateCustomer({
        customerId: res.customerId,
        custom_user_agent: navigator.userAgent,
        custom_http_referer: `${window.location.origin}${window.location.pathname}?${persistedUrlParameters}`,
        ...customCustomerParams,
      });

      if (successRedirect) {
        navigate(successRedirect, { orderId: res?.orderId });
      }
    } catch (e: any) {
      Errors.report(e, {
        geo: window.GEO,
        inputProps: props,
        product: product || 'undefined',
        config: this.fc.config || 'undefined',
        lead: this.fc.lead.lead || 'undefined',
        productIdx: window['productIdx'].value || 'undefined',
      });

      const { orderId } = this.getParams();
      SiftEvents.trackFailedTransaction(orderId, this.confirm_pp_p, {
        paymentType: PaySource.PAYPAL,
        errorMessage: `${e?.response?.data?.message[0]}`,
      });
    } finally {
      NON_RESPONSIVE_TS && clearTimeout(NON_RESPONSIVE_TS);
    }
    UiController.showPpLoaderModal(false);
  };

  createKlarnaOrder = async () => {
    try {
      if (!this.klarna) {
        throw new Error('Klarna service undefined');
      }
      const {
        auid,
        lead,
        product,
        sessionId,
        successRedirect,
        customPayloadParams,
      } = this.getParams();

      validateLead(lead);
      const { order, klarnaApproved } = await this.klarna.authorizeOrder(
        product,
      );

      if (!klarnaApproved) {
        return;
      }

      const customerTokenResponse = await Api.getCustomerToken(order);
      const klarnaOrderResponse = await Api.createKlarnaPaymentsOrder(order);

      if (klarnaOrderResponse?.order_id) {
        NON_RESPONSIVE_TS = Errors.maybeReportNonResponsivePaymentButton(
          PaySource.KLARNA,
        );

        this._setSelectedProductCookie();

        const optinCheckboxes =
          getVisibleElementsById<'input'>('optinCheckbox');
        const optinCheckbox =
          optinCheckboxes?.filter(el => el?.checked)[0] || optinCheckboxes[0];

        const orderData: any = {
          client: window.BRAND,
          fullUrl: location.href,
          orderId: this.fc.lead.lead.orderId || '',
          emailAddress: this.fc.lead.lead.emailAddress,
          product1_id: String(product.id),
          product1_price: product.discountPrice
            ? product.finalPrice - product.discountPrice
            : product.finalPrice,
          klarnaOrderId: klarnaOrderResponse.order_id,
          quantity: product.quantity,
          klarnaAuthToken: 'token',
          product1_shipPrice: product.shippingPrice || 0,
          paySource: PaySource.PREPAID,
          campaignId: this.fc.config.campaignId,
          custom1: getUrlParameters('ad_id'),
          custom2: 'klarna',
          custom3: `${window.location.origin + window.location.pathname}`,
          sourceValue3: auid,
          sourceValue4: `id=${this.fc.lead.lead.orderId}`,
          companyName: 'unfullfiled',
          currency: product.currencyCode,
          custom_order_auid: auid,
          custom_order_sid: sessionId,
          custom_order_paymentType: PaySource.KLARNA,
          custom_order_initial: product.id.toString(),
          custom_order_paymentMethod: PaySource.KLARNA,
          custom_order_freeGift: product?.freeGift || false,
          custom_order_klarnaToken: customerTokenResponse?.token_id,
          custom_order_marketing_opt_in: optinCheckbox
            ? `${optinCheckbox.checked}`
            : `0`,
          custom_order_order_flow: window.IS_1_CLICK
            ? OrderFlow.CHECKOUT_1CLICK
            : OrderFlow.CHECKOUT,
          note: 'initial',
          userAgent: navigator?.userAgent,
          ipAddress: window.GEO?.ip || '::1',
          custom_order_force: new URL(location.href).searchParams.get('force') || '',
          ...customPayloadParams,
        };

        this.fc.addAddonsToPurchasePayload(orderData);
        this._addExtraParameters(orderData, 'klarna');

        // Track create_order event in Sift with orderData containing all products
        SiftEvents.createOrder(
          { ...this.fc.lead.lead, ...orderData },
          { paymentType: PaySource.KLARNA, returnScore: false },
        );

        const res = await Api.importKlarnaOrder(orderData);

        SiftEvents.trackTransaction(orderData, res, {
          returnScore: true,
          paymentType: PaySource.KLARNA,
        });

        if (window.DEBUG) {
          console.log(res);
        }

        navigate(successRedirect, this.fc.lead.lead);
      } else {
        throw new Error('Invalid Klarna order response');
      }
    } catch (e: any) {
      Errors.report(e, {
        geo: window.GEO,
        config: this.fc.config || 'undefined',
        lead: this.fc.lead.lead || 'undefined',
        product: this.fc.getSelectedProduct() || 'undefined',
        productIdx: window['productIdx'].value || 'undefined',
      });

      const { orderId } = this.getParams();
      SiftEvents.trackFailedTransaction(orderId, null, {
        paymentType: PaySource.KLARNA,
        errorMessage: `${e?.response?.data?.message[0]}`,
      });
    } finally {
      NON_RESPONSIVE_TS && clearTimeout(NON_RESPONSIVE_TS);
    }
  };

  createApplePayOrderExpress = async (
    nonce: string,
    session: ApplePaySession,
    contactInformation: {
      emailAddress: string;
      familyName: string;
      givenName: string;
      addressLines: [key: string];
      locality: string;
      administrativeArea: string;
      postalCode: string;
      countryCode: string;
      phoneNumber: string;
    },
  ) => {
    const locale = this.fc.config?.locale || '';
    const { product, customPayloadParams } = this.getParams();
    const urlQueryParameters = getCustomCookieParams();
    const optinCheckboxes = getVisibleElementsById<'input'>('optinCheckbox');
    const optinCheckbox =
      optinCheckboxes?.filter(el => el?.checked)[0] || optinCheckboxes[0];

    const trackStartedCheckoutV2 = () => {
      Klaviyo.trackStartedCheckoutV2(
        contactInformation.emailAddress,
        contactInformation.givenName || contactInformation.familyName,
        contactInformation.familyName || contactInformation.givenName,
        product,
        locale,
      );
    };

    try {
      const params = new URLSearchParams(document.location.search);
      const { auid, lead, sessionId, successRedirect } = this.getParams();
      NON_RESPONSIVE_TS = Errors.maybeReportNonResponsivePaymentButton(
        PaySource.APPLEPAY,
      );

      this._setSelectedProductCookie();

      const orderData: any = {
        client: window.BRAND,
        affId: params.get('affid') || undefined,
        paySource: PaySource.APPLEPAY,
        campaignId: this.fc.config.campaignId,
        product1_id: String(product.id),
        product1_price: product.discountPrice
          ? product.finalPrice - product.discountPrice
          : product.finalPrice,
        product1_shipPrice: product.shippingPrice || 0,
        emailAddress: contactInformation.emailAddress,
        sessionId,
        firstName:
          contactInformation.givenName || contactInformation.familyName,
        lastName: contactInformation.familyName || contactInformation.givenName,
        address1: contactInformation?.addressLines[0]
          ? contactInformation?.addressLines[0]
          : '',
        city: contactInformation.locality,
        state: contactInformation.administrativeArea || '',
        postalCode: contactInformation.postalCode || '',
        country: contactInformation.countryCode || '',
        phoneNumber: contactInformation.phoneNumber || '',
        custom1: getUrlParameters('ad_id'),
        forceMerchantId: Number(this.fc.config?.appleGooglePayMerchantId),
        companyName: 'unfulfilled',
        fullUrl: location.href,
        applePayToken: nonce,
        sourceValue3: auid,
        custom_order_sid: sessionId,
        custom2: 'apple_pay_express',
        custom3: `${window.location.origin + window.location.pathname}`,
        custom_order_initial: product.id.toString(),
        custom_order_language: this.fc.config.locale,
        custom_order_paymentType: 'apple_pay_express',
        custom_order_paymentMethod: PaySource.APPLEPAY,
        custom_order_order_flow: window.IS_1_CLICK
          ? OrderFlow.CHECKOUT_1CLICK
          : OrderFlow.CHECKOUT,
        note: 'initial',
        ...getDeviceInfo(),
        custom_order_marketing_opt_in: optinCheckbox
          ? `${optinCheckbox.checked}`
          : `0`,
        ...urlQueryParameters,
        userAgent: navigator?.userAgent,
        ipAddress: window.GEO?.ip || '::1',
        custom_order_force: new URL(location.href).searchParams.get('force') || '',
        ...customPayloadParams,
      };

      this.fc.addAddonsToPurchasePayload(orderData);
      this._addExtraParameters(orderData, 'applepay_express');

      // Track create_order event in Sift with orderData containing all products
      SiftEvents.createOrder(
        { ...this.fc.lead.lead, ...orderData },
        {
          paymentType: PaySource.APPLEPAY,
          isExpressPayment: true,
          returnScore: false,
        },
      );

      const res = await Api.importApplePayOrderExpress(orderData);

      SiftEvents.trackTransaction(orderData, res, {
        isExpressPayment: true,
        returnScore: true,
        paymentType: PaySource.APPLEPAY,
      });

      trackStartedCheckoutV2();

      Api.updateCustomer({
        customerId: res.customerId,
        custom_user_agent: navigator.userAgent,
        custom_http_referer: window.location.href,
        ...getAppUtms(),
      });

      if (window.DEBUG) {
        console.log(res);
      }

      const updatedLead = {
        ...lead,
        sessionId,
        orderId: res?.orderId,
      };

      session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
      navigate(successRedirect, updatedLead);
    } catch (e: any) {
      trackStartedCheckoutV2();
      session.abort();
      Errors.report(e, {
        geo: window.GEO,
        config: this.fc.config || 'undefined',
        lead: this.fc.lead.lead || 'undefined',
        product: this.fc.getSelectedProduct() || 'undefined',
        productIdx: window['productIdx'].value || 'undefined',
      });

      const { orderId } = this.getParams();
      SiftEvents.trackFailedTransaction(orderId, null, {
        paymentType: PaySource.APPLEPAY,
        errorMessage: `${e?.response?.data?.message[0]}`,
      });
    } finally {
      NON_RESPONSIVE_TS && clearTimeout(NON_RESPONSIVE_TS);
    }
  };

  createStripeApplePayOrder = async (
    nonce: string,
    event: any,
    contactInformation: {
      emailAddress: string;
      familyName: string;
      givenName: string;
      addressLines: string[] | undefined;
      locality: string;
      administrativeArea: string;
      postalCode: string;
      countryCode: string;
      phoneNumber: string;
    },
  ) => {
    const locale = this.fc.config?.locale || '';
    const { product, customPayloadParams } = this.getParams();
    const urlQueryParameters = getCustomCookieParams();
    const optinCheckboxes = getVisibleElementsById<'input'>('optinCheckbox');
    const optinCheckbox =
      optinCheckboxes?.filter(el => el?.checked)[0] || optinCheckboxes[0];

    const trackStartedCheckoutV2 = () => {
      Klaviyo.trackStartedCheckoutV2(
        contactInformation.emailAddress,
        contactInformation.givenName || contactInformation.familyName,
        contactInformation.familyName || contactInformation.givenName,
        product,
        locale,
      );
    };

    try {
      const params = new URLSearchParams(document.location.search);
      const { auid, lead, sessionId, successRedirect } = this.getParams();
      NON_RESPONSIVE_TS = Errors.maybeReportNonResponsivePaymentButton(
        PaySource.APPLEPAY,
      );

      this._setSelectedProductCookie();

      const orderData: any = {
        client: window.BRAND,
        affId: params.get('affid') || undefined,
        paySource: PaySource.APPLEPAY,
        campaignId: this.fc.config.campaignId,
        product1_id: String(product.id),
        product1_price: product.discountPrice
          ? product.finalPrice - product.discountPrice
          : product.finalPrice,
        product1_shipPrice: product.shippingPrice || 0,
        emailAddress: contactInformation.emailAddress,
        sessionId,
        firstName:
          contactInformation.givenName || contactInformation.familyName,
        lastName: contactInformation.familyName || contactInformation.givenName,
        address1: contactInformation?.addressLines?.[0]
          ? contactInformation?.addressLines[0]
          : '',
        city: contactInformation.locality,
        state: contactInformation.administrativeArea || '',
        postalCode: contactInformation.postalCode || '',
        country: contactInformation.countryCode || '',
        phoneNumber: contactInformation.phoneNumber || '',
        custom1: getUrlParameters('ad_id'),
        forceMerchantId: Number(this.fc.config?.appleGooglePayMerchantId),
        companyName: 'unfulfilled',
        fullUrl: location.href,
        applePayToken: nonce,
        sourceValue3: auid,
        custom_order_sid: sessionId,
        custom2: 'apple_pay_express',
        custom3: `${window.location.origin + window.location.pathname}`,
        custom_order_initial: product.id.toString(),
        custom_order_language: this.fc.config.locale,
        custom_order_paymentType: 'apple_pay_express',
        custom_order_paymentMethod: PaySource.APPLEPAY,
        custom_order_order_flow: window.IS_1_CLICK
          ? OrderFlow.CHECKOUT_1CLICK
          : OrderFlow.CHECKOUT,
        note: 'initial',
        ...getDeviceInfo(),
        custom_order_marketing_opt_in: optinCheckbox
          ? `${optinCheckbox.checked}`
          : `0`,
        ...urlQueryParameters,
        userAgent: navigator?.userAgent,
        ipAddress: window.GEO?.ip || '::1',
        custom_order_force: new URL(location.href).searchParams.get('force') || '',
        ...customPayloadParams,
      };

      this.fc.addAddonsToPurchasePayload(orderData);
      this._addExtraParameters(orderData, 'applepay_stripe');

      // Track create_order event in Sift with orderData containing all products
      SiftEvents.createOrder(
        { ...this.fc.lead.lead, ...orderData },
        {
          paymentType: PaySource.APPLEPAY,
          isExpressPayment: true,
          returnScore: false,
        },
      );

      const res = await Api.importApplePayOrderExpress(orderData);

      SiftEvents.trackTransaction(orderData, res, {
        isExpressPayment: true,
        returnScore: true,
        paymentType: PaySource.APPLEPAY,
      });

      trackStartedCheckoutV2();

      Api.updateCustomer({
        customerId: res.customerId,
        custom_user_agent: navigator.userAgent,
        custom_http_referer: window.location.href,
        ...getAppUtms(),
      });

      if (window.DEBUG) {
        console.log(res);
      }

      const updatedLead = {
        ...lead,
        sessionId,
        orderId: res?.orderId,
      };

      event.complete('success');
      navigate(successRedirect, updatedLead);
    } catch (e: any) {
      trackStartedCheckoutV2();
      event.complete('fail');
      Errors.report(e, {
        geo: window.GEO,
        config: this.fc.config || 'undefined',
        lead: this.fc.lead.lead || 'undefined',
        product: this.fc.getSelectedProduct() || 'undefined',
        productIdx: window['productIdx'].value || 'undefined',
      });

      const { orderId } = this.getParams();
      SiftEvents.trackFailedTransaction(orderId, null, {
        paymentType: PaySource.APPLEPAY,
        errorMessage: `${e?.response?.data?.message[0]}`,
      });
    } finally {
      NON_RESPONSIVE_TS && clearTimeout(NON_RESPONSIVE_TS);
    }
  };

  createApplePayOrder = async (nonce: string, session: ApplePaySession) => {
    try {
      const {
        auid,
        lead,
        orderId,
        product,
        sessionId,
        successRedirect,
        customPayloadParams,
      } = this.getParams();
      if (!lead) {
        session.abort();
        return;
      }

      NON_RESPONSIVE_TS = Errors.maybeReportNonResponsivePaymentButton(
        PaySource.APPLEPAY,
      );

      this._setSelectedProductCookie();

      const orderData: any = {
        orderId,
        custom2: 'apple_pay',
        custom3: `${window.location.origin + window.location.pathname}`,
        client: window.BRAND,
        applePayToken: nonce,
        fullUrl: location.href,
        companyName: 'unfulfilled',
        paySource: PaySource.APPLEPAY,
        product1_id: String(product.id),
        custom1: getUrlParameters('ad_id'),
        product1_price: product.discountPrice
          ? product.finalPrice - product.discountPrice
          : product.finalPrice,
        emailAddress: lead.emailAddress || '',
        campaignId: this.fc.config.campaignId,
        product1_shipPrice: product.shippingPrice || 0,
        forceMerchantId: Number(this.fc.config?.appleGooglePayMerchantId),
        sourceValue3: auid,
        custom_order_auid: auid,
        custom_order_sid: sessionId,
        custom_order_paymentType: 'apple_pay',
        custom_order_initial: product.id.toString(),
        custom_order_paymentMethod: PaySource.APPLEPAY,
        custom_order_freeGift: product?.freeGift || false,
        custom_order_order_flow: window.IS_1_CLICK
          ? OrderFlow.CHECKOUT_1CLICK
          : OrderFlow.CHECKOUT,
        note: 'initial',
        userAgent: navigator?.userAgent,
        ipAddress: window.GEO?.ip || '::1',
        custom_order_force: new URL(location.href).searchParams.get('force') || '',
        ...customPayloadParams,
      };

      this.fc.addAddonsToPurchasePayload(orderData);
      this._addExtraParameters(orderData, 'applepay');

      // Track create_order event in Sift with orderData
      SiftEvents.createOrder(orderData, {
        paymentType: PaySource.APPLEPAY,
        returnScore: false,
      });

      const res = await Api.importApplePayOrder(orderData);

      SiftEvents.trackTransaction(orderData, res, {
        isExpressPayment: true,
        returnScore: true,
        paymentType: PaySource.APPLEPAY,
      });

      if (window.DEBUG) {
        console.log(res);
      }

      const updatedLead = {
        ...lead,
        sessionId,
        orderId: res?.orderId,
      };

      session.completePayment(window.ApplePaySession.STATUS_SUCCESS);
      navigate(successRedirect, updatedLead);
    } catch (e) {
      session.abort();
      Errors.report(e, {
        geo: window.GEO,
        config: this.fc.config || 'undefined',
        lead: this.fc.lead.lead || 'undefined',
        product: this.fc.getSelectedProduct() || 'undefined',
        productIdx: window['productIdx'].value || 'undefined',
      });
    } finally {
      NON_RESPONSIVE_TS && clearInterval(NON_RESPONSIVE_TS);
    }
  };

  initAppleSession = async (
    leadCb: (
      paymentType: GaPaymentButtonType,
      validate: boolean,
      mid?: string | number,
    ) => Promise<IImportLeadPayload | null>,
    expressButton?: boolean,
  ) => {
    const session = await this.braintree?.createAppleSession(
      this,
      this.fc.config.expressApplePay,
    );
    session?.begin();

    if (!expressButton) {
      const mid = this.fc.config.appleGooglePayMerchantId;
      const lead = await leadCb(`apple_pay`, true, mid);
      if (!lead) {
        session?.abort();
      }
    }
  };

  initStripeApplePay = async (expressButton?: boolean) => {
    if (window.DEBUG) {
      // Maybe remove latter
      console.log(expressButton);
    }
    this.stripeApplePay?.createApplePayment(
      this,
      this.fc.config.expressApplePay,
    );
  };

  createGooglePayOrder = async () => {
    try {
      const {
        auid,
        lead,
        orderId,
        product,
        sessionId,
        successRedirect,
        customPayloadParams,
      } = this.getParams();
      const result = await this.braintree?.createGPayOrder(product);
      if (!result?.nonce) return;

      NON_RESPONSIVE_TS = Errors.maybeReportNonResponsivePaymentButton(
        PaySource.GOOGLEPAY,
      );

      const orderData: any = {
        orderId,
        client: window.BRAND,
        fullUrl: location.href,
        paySource: PaySource.GOOGLEPAY,
        product1_id: String(product.id),
        emailAddress: lead.emailAddress,
        product1_price: product.discountPrice
          ? product.finalPrice - product.discountPrice
          : product.finalPrice,
        campaignId: this.fc.config.campaignId,
        product1_shipPrice: product.shippingPrice || 0,
        custom1: getUrlParameters('ad_id'),
        forceMerchantId: Number(
          this.fc.config?.braintreeAppleGooglePayMerchantId
            ? this.fc.config?.braintreeAppleGooglePayMerchantId
            : this.fc.config?.appleGooglePayMerchantId,
        ),
        companyName: 'unfulfilled',
        googlePayToken: result?.nonce || '',
        custom2: 'google_pay',
        custom3: `${window.location.origin + window.location.pathname}`,
        custom_order_paymentType: 'google_pay',
        sourceValue3: auid,
        custom_order_auid: auid,
        custom_order_sid: sessionId,
        custom_order_initial: product.id.toString(),
        custom_order_paymentMethod: PaySource.GOOGLEPAY,
        custom_order_freeGift: product?.freeGift || false,
        custom_order_order_flow: window.IS_1_CLICK
          ? OrderFlow.CHECKOUT_1CLICK
          : OrderFlow.CHECKOUT,
        note: 'initial',
        userAgent: navigator?.userAgent,
        ipAddress: window.GEO?.ip || '::1',
        custom_order_force: new URL(location.href).searchParams.get('force') || '',
        ...customPayloadParams,
      };

      this.fc.addAddonsToPurchasePayload(orderData);
      this._addExtraParameters(orderData, 'googlepay');

      // Track create_order event in Sift with orderData
      SiftEvents.createOrder(orderData, {
        paymentType: PaySource.GOOGLEPAY,
        returnScore: false,
      });

      const res = await Api.importGooglePayOrder(orderData);

      SiftEvents.trackTransaction(orderData, res, {
        isExpressPayment: true,
        returnScore: true,
        paymentType: PaySource.GOOGLEPAY,
      });

      if (window.DEBUG) {
        console.log(res);
      }

      navigate(successRedirect, this.fc.lead.lead);
    } catch (e: any) {
      Errors.report(e, {
        geo: window.GEO,
        config: this.fc.config || 'undefined',
        lead: this.fc.lead.lead || 'undefined',
        product: this.fc.getSelectedProduct() || 'undefined',
        productIdx: window['productIdx'].value || 'undefined',
      });

      const { orderId } = this.getParams();
      SiftEvents.trackFailedTransaction(orderId, null, {
        paymentType: PaySource.GOOGLEPAY,
        errorMessage: `${e?.response?.data?.message[0]}`,
      });
    } finally {
      NON_RESPONSIVE_TS && clearTimeout(NON_RESPONSIVE_TS);
    }
  };

  createExpressKlarnaOrder = async (authorize: any, callback?: any) => {
    try {
      if (!this.klarna) {
        throw new Error('Klarna service undefined');
      }

      const { auid, product, sessionId, successRedirect, customPayloadParams } =
        this.getParams();
      const order: any = this.klarna.createExpressKlarnaOrderObj(product);

      this._setSelectedProductCookie();

      await authorize(
        { auto_finalize: true, collect_shipping_address: true },
        order,
        async (result: any) => {
          if (window.DEBUG) {
            console.log('result: ', result);
          }

          if (result.authorization_token && result.collected_shipping_address) {
            this.fc.lead.setLeadInfo(
              result.collected_shipping_address.given_name,
              result.collected_shipping_address.family_name,
              result.collected_shipping_address.postal_code,
              result.collected_shipping_address.city,
              result.collected_shipping_address.country,
              result.collected_shipping_address.email,
              result.collected_shipping_address.phone,
              result.collected_shipping_address.street_address,
            );
            await this.fc.lead.prefillForm();

            const orderPayload: any = this.klarna?.createExpressKlarnaOrderObj(
              product,
              result.authorization_token,
              result.collected_shipping_address,
            );
            const customerTokenResponse = await Api.getCustomerToken(
              orderPayload,
            );
            const klarnaOrderResponse = await Api.createKlarnaPaymentsOrder(
              orderPayload,
            );

            const optinCheckboxes =
              getVisibleElementsById<'input'>('optinCheckbox');
            const optinCheckbox =
              optinCheckboxes?.filter(el => el?.checked)[0] ||
              optinCheckboxes[0];

            if (klarnaOrderResponse?.order_id) {
              const lead = await this.fc.lead.createLead('PREPAID', false);
              if (lead) {
                const orderData: any = {
                  client: window.BRAND,
                  fullUrl: location.href,
                  orderId: this.fc.lead.lead.orderId || '',
                  emailAddress: this.fc.lead.lead.emailAddress,
                  product1_id: String(product.id),
                  product1_price: product.discountPrice
                    ? product.finalPrice - product.discountPrice
                    : product.finalPrice,
                  klarnaOrderId: klarnaOrderResponse.order_id,
                  quantity: product.quantity,
                  klarnaAuthToken: 'token',
                  product1_shipPrice: product.shippingPrice || 0,
                  paySource: PaySource.PREPAID,
                  campaignId: this.fc.config.campaignId,
                  custom1: getUrlParameters('ad_id'),
                  custom2: 'klarna',
                  custom3: `${
                    window.location.origin + window.location.pathname
                  }`,
                  sourceValue3: auid,
                  sourceValue4: `id=${this.fc.lead.lead.orderId}`,
                  companyName: 'unfullfiled',
                  currency: product.currencyCode,
                  custom_order_language: this.fc.config.locale,
                  custom_order_auid: auid,
                  custom_order_sid: sessionId,
                  custom_order_paymentType: PaySource.KLARNA,
                  custom_order_initial: product.id.toString(),
                  custom_order_paymentMethod: PaySource.KLARNA,
                  custom_order_freeGift: product?.freeGift || false,
                  custom_order_klarnaToken: customerTokenResponse?.token_id,
                  custom_order_order_flow: window.IS_1_CLICK
                    ? OrderFlow.CHECKOUT_1CLICK
                    : OrderFlow.CHECKOUT,
                  ...getDeviceInfo(),
                  custom_order_marketing_opt_in: optinCheckbox
                    ? `${optinCheckbox.checked}`
                    : `0`,
                  note: 'initial',
                  userAgent: navigator?.userAgent,
                  ipAddress: window.GEO?.ip || '::1',
                  custom_order_force: new URL(location.href).searchParams.get('force') || '',
                  ...customPayloadParams,
                };

                this.fc.addAddonsToPurchasePayload(orderData);
                this._addExtraParameters(orderData, 'klarna_express');

                SiftEvents.createOrder(orderData, {
                  paymentType: PaySource.KLARNA,
                  isExpressPayment: true,
                  returnScore: false,
                });

                const res = await Api.importKlarnaOrder(orderData);

                SiftEvents.trackTransaction(orderData, res, {
                  isExpressPayment: true,
                  returnScore: true,
                  paymentType: PaySource.KLARNA,
                });

                if (callback) {
                  callback(result);
                }

                navigate(successRedirect, this.fc.lead.lead);
              } else {
                throw new Error('Invalid Express Klarna order response');
              }
            } else {
              if (callback) {
                callback(result);
              }
            }
          } else {
            if (callback) {
              callback(result);
            }
          }
        },
      );
    } catch (e: any) {
      Errors.report(e, {
        geo: window.GEO,
        config: this.fc.config || 'undefined',
        lead: this.fc.lead.lead || 'undefined',
        product: this.fc.getSelectedProduct() || 'undefined',
        productIdx: window['productIdx'].value || 'undefined',
      });

      const { orderId } = this.getParams();
      SiftEvents.trackFailedTransaction(orderId, null, {
        paymentType: PaySource.KLARNA,
        errorMessage: `${e?.response?.data?.message[0]}`,
      });
    }
  };
}
